import { isEmpty, toString } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  AssemblyJourneys,
  CodJourney,
  SectionJourney,
} from 'src/constants/assemblyJourneys';
import {
  useGetAssembly,
  useGetLayoutScreens,
  useGetScheduleAssemblies,
  useUpdateAssembly,
  useUpdateCreateDeleteAssemblyComponents,
  useViewAssemblyComponents,
} from 'src/hooks';
import { useGetBulkReplaceRequestNamesByAssemblyIds } from 'src/hooks/api/domainSpecific/bulkReplace/useGetBulkReplaceRequestNamesByAssemblyIds';
import { useApiErrorNavigator } from 'src/hooks/navigation';
import { useCodEvents } from 'src/hooks/useCodEvents';
import { useDaypartGlobalComponent } from 'src/hooks/useDaypartGlobalComponent';
import { useUserRole } from 'src/utils';
import { GcfMockData } from 'src/utils/gcf-helper';
import { checkSectionTypes } from 'src/utils/section-types-helper';

export const useDetailAssemblyPage = () => {
  const [activeAssemblyJourney, setActiveAssemblyJourney] = useState<
    AssemblyJourneys | undefined
  >();

  const [customerState, setCustomerState] = useState<Partial<GcfMockData>>(
    GcfMockData.COD_CUSTOMER_UNKNOWN,
  );
  const isDeleted = useRef(false);
  const { isOwnerOperator } = useUserRole();

  const {
    assemblyId = '',
    locationCode = '',
    scheduleAssemblyId = '',
  } = useParams();

  const {
    data: [scheduleAssembly] = [],
    isLoading: isLoadingScheduleAssemblies,
  } = useGetScheduleAssemblies(
    [`id=${scheduleAssemblyId}`],
    !isEmpty(scheduleAssemblyId) && !isDeleted.current,
  );

  const assembly_id = assemblyId || scheduleAssembly?.assembly?.id || '';

  const bulkReplaceRequestAssemblyId = isEmpty(assemblyId)
    ? Number(scheduleAssemblyId)
    : Number(assemblyId);

  const { data: stagedAssemblies } = useGetBulkReplaceRequestNamesByAssemblyIds(
    {
      assembly_ids: [bulkReplaceRequestAssemblyId],
      statuses: ['staged'],
    },
    bulkReplaceRequestAssemblyId > 0 && !isOwnerOperator,
  );

  const { data: savedAssemblies } = useGetBulkReplaceRequestNamesByAssemblyIds(
    {
      assembly_ids: [bulkReplaceRequestAssemblyId],
      statuses: ['saved'],
    },
    bulkReplaceRequestAssemblyId > 0 && !isOwnerOperator,
  );

  const stagedAssemblyIds = new Set(
    stagedAssemblies?.map(
      (item: { assembly_id: number }) => item.assembly_id,
    ) ?? [],
  );

  const savedAssemblyIds = new Set(
    savedAssemblies?.map((item: { assembly_id: number }) => item.assembly_id) ??
      [],
  );

  const isStageAssemblyIdMatched = stagedAssemblyIds.has(
    bulkReplaceRequestAssemblyId,
  );
  const isSavedAssemblyIdMatched = savedAssemblyIds.has(
    bulkReplaceRequestAssemblyId,
  );

  useEffect(() => {
    setActiveAssemblyJourney(CodJourney.CUSTOMER_ENTERS_DRIVE_THRU);
  }, [assembly_id]);

  const updateAssembly = useUpdateAssembly();
  const updateCreateDeleteAssemblyComponents =
    useUpdateCreateDeleteAssemblyComponents();

  const {
    data: assemblyDetails,
    isLoading: isAssemblyDetailsLoading,
    error,
  } = useGetAssembly(toString(assembly_id), !isDeleted.current);

  useApiErrorNavigator(error);

  const { data: layouts = [], isLoading: isLayoutScreensLoading } =
    useGetLayoutScreens(
      assemblyDetails?.layout_id,
      Boolean(assemblyDetails?.layout_id),
    );

  const { globalComponentsData, onChangeDaypart, selectedDaypartId } =
    useDaypartGlobalComponent(layouts?.[0]?.layout_type_id);

  useCodEvents(
    customerState,
    activeAssemblyJourney as CodJourney,
    selectedDaypartId,
  );

  const {
    data: [assemblyComponentsDetails] = [],
    isLoading: isViewAssemblyComponentsLoading,
  } = useViewAssemblyComponents(
    [`id=${assembly_id}`, 'omitCovered=true'],
    Boolean(assembly_id),
  );

  const isLoadingAssemblyOverrides =
    isLayoutScreensLoading ||
    isAssemblyDetailsLoading ||
    isViewAssemblyComponentsLoading ||
    (scheduleAssemblyId && isLoadingScheduleAssemblies);

  useEffect(() => {
    const sectionTypes = layouts?.[0]?.config?.sectionTypes;
    const { hasJourney } = checkSectionTypes(sectionTypes);
    setActiveAssemblyJourney(
      hasJourney
        ? CodJourney.CUSTOMER_ENTERS_DRIVE_THRU
        : SectionJourney.DEFAULT,
    );
  }, [layouts]);

  return {
    assemblyDetails,
    scheduleAssemblyId,
    assemblyComponentsDetails,
    updateAssembly,
    locationCode,
    updateCreateDeleteAssemblyComponents,
    layouts,
    isLoadingAssemblyOverrides,
    isAssemblyDetailsLoading,
    activeAssemblyJourney,
    setActiveAssemblyJourney,
    customerState,
    setCustomerState,
    isDeleted,
    assemblyId,
    isStageAssemblyIdMatched,
    isSavedAssemblyIdMatched,
    globalComponentsData,
    onChangeDaypart,
    selectedDaypartId,
  };
};
